import React, {FC, useState, useEffect} from 'react'
import {useLocation} from 'react-router-dom'

import TrelloOAuthModal from './TrelloOAuthModal'
import {trelloApiService, TrelloIntegrationStatus} from '../services/trelloApi'

interface TrelloIntegrationCardProps {}

const TrelloIntegrationCard: FC<TrelloIntegrationCardProps> = () => {
  const location = useLocation()

  const [showOAuthModal, setShowOAuthModal] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [lastSync, setLastSync] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [integrationStatus, setIntegrationStatus] = useState<TrelloIntegrationStatus | null>(null)
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null)

  // Carrega o status da integração ao montar o componente
  useEffect(() => {
    // Usa a mesma chave de autenticação do sistema
    const authKey = 'impersonating-auth-token';
    const authData = localStorage.getItem(authKey);
    
    let token = null;
    if (authData) {
      try {
        const auth = JSON.parse(authData);
        token = auth.api_token;
      } catch (error) {
        console.error('Erro ao parsear dados de autenticação:', error);
      }
    }
    
    // Fallback para outras chaves
    if (!token) {
      token = localStorage.getItem('access_token') || 
              localStorage.getItem('token') || 
              localStorage.getItem('auth_token');
    }
    
    if (token) {
      loadIntegrationStatus()
    }
  }, [])

  // Processa mensagens do estado da rota (callback do Trello)
  useEffect(() => {
    if (location.state) {
      const {success, error, message: routeMessage} = location.state as any
      
      if (success && routeMessage) {
        setMessage({type: 'success', text: routeMessage})
        // Recarrega o status da integração
        loadIntegrationStatus()
      } else if (error && routeMessage) {
        setMessage({type: 'error', text: routeMessage})
      }
      
      // Limpa o estado da rota
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  const loadIntegrationStatus = async () => {
    try {
      // Usa a mesma chave de autenticação do sistema
      const authKey = 'impersonating-auth-token';
      const authData = localStorage.getItem(authKey);
      
      let token = null;
      if (authData) {
        try {
          const auth = JSON.parse(authData);
          token = auth.api_token;
        } catch (error) {
          console.error('Erro ao parsear dados de autenticação:', error);
        }
      }
      
      // Fallback para outras chaves
      if (!token) {
        token = localStorage.getItem('access_token') || 
                localStorage.getItem('token') || 
                localStorage.getItem('auth_token');
      }
      
      if (!token) {
        return
      }
      
      setIsLoading(true)
      const status = await trelloApiService.getStatus()
      console.log('Trello Integration Status:', status)
      setIntegrationStatus(status)
      console.log('Setting isConnected to:', status.is_connected)
      setIsConnected(status.is_connected)
      setLastSync(status.integration?.last_sync_at || null)
      
      // Limpa mensagens de erro se a integração estiver funcionando
      if (status.is_connected) {
        setMessage(null)
      }
    } catch (error: any) {
      console.error('Error loading integration status:', error)
      // Não mostra erro se for 401 (não autorizado) - é esperado sem token
      if (error?.response?.status !== 401) {
        console.error('Erro inesperado ao carregar status:', error)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleConnect = async () => {
    // Sempre mostra o modal OAuth, que irá verificar as chaves internamente
    setShowOAuthModal(true)
  }

  const handleDisconnect = async () => {
    try {
      setIsLoading(true)
      await trelloApiService.disconnect()
      setIsConnected(false)
      setLastSync(null)
      setIntegrationStatus(null)
    } catch (error) {
      console.error('Error disconnecting from Trello:', error)
      alert('Erro ao desconectar do Trello. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }



  return (
    <>
      <div className='card h-100 w-100 d-flex flex-column'>
        <div className='card-header border-0 pt-5'>
          <div className='d-flex align-items-center'>
            <div className='symbol symbol-50px me-5'>
              <div className='symbol-label bg-light-primary'>
                <i className='fab fa-trello fs-2x' style={{color: '#5d7a1f'}}></i>
              </div>
            </div>
            <div className='d-flex flex-column'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-4 mb-2'>Trello</span>
                <span className='text-muted mt-1 fw-semibold fs-7'>
                  Gerenciamento de projetos visual
                </span>
              </h3>
            </div>
          </div>
        </div>
        
        <div className='card-body pt-0 d-flex flex-column flex-grow-1'>
          <div className='mb-6 flex-grow-1'>
            <p className='text-muted fs-7 mb-4'>
              Importe boards, listas e cards do Trello para o TaskRush. 
              Mantenha seus projetos sincronizados entre as plataformas.
            </p>
            
            {/* Mensagens de sucesso/erro */}
            {message && (
              <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} mb-4`}>
                <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
                {message.text}
                <button 
                  type='button' 
                  className='btn-close ms-auto' 
                  onClick={() => setMessage(null)}
                ></button>
              </div>
            )}
            
            <div className='d-flex flex-column gap-3'>
              <div className='d-flex align-items-center justify-content-between'>
                <span className='text-muted fs-7'>Status:</span>
                <span className={`badge ${isConnected ? 'badge-light-success' : 'badge-light-warning'}`}>
                  {isConnected ? 'Conectado' : 'Desconectado'}
                </span>
              </div>
              
              {integrationStatus?.integration?.trello_username && (
                <div className='d-flex align-items-center justify-content-between'>
                  <span className='text-muted fs-7'>Usuário Trello:</span>
                  <span className='text-muted fs-7'>@{integrationStatus.integration.trello_username}</span>
                </div>
              )}
              
              {lastSync && (
                <div className='d-flex align-items-center justify-content-between'>
                  <span className='text-muted fs-7'>Última sincronização:</span>
                  <span className='text-muted fs-7'>
                    {new Date(lastSync).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          <div className='mt-auto'>
            <div className='d-flex flex-column gap-2'>
              {!isConnected ? (
                <button
                  className='btn btn-sm btn-primary w-100'
                  onClick={handleConnect}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                      Conectando...
                    </>
                  ) : (
                    <>
                      <i className='fas fa-plug me-2'></i>
                      Conectar com Trello
                    </>
                  )}
                </button>
              ) : (
                <>
                  <button
                    className='btn btn-sm btn-light-danger w-100'
                    onClick={handleDisconnect}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                        Desconectando...
                      </>
                    ) : (
                      <>
                        <i className='fas fa-unlink me-2'></i>
                        Desconectar com o Trello
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <TrelloOAuthModal
        show={showOAuthModal}
        onClose={() => setShowOAuthModal(false)}
        onSuccess={(integration) => {
          setMessage({type: 'success', text: 'Trello conectado com sucesso!'})
          loadIntegrationStatus()
        }}
      />
    </>
  )
}

export default TrelloIntegrationCard
